// Simple JWT-based auth - no Microsoft, no MSAL

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Token storage
const getToken = () => localStorage.getItem('heyday_token');
const setToken = (token) => localStorage.setItem('heyday_token', token);
const clearToken = () => localStorage.removeItem('heyday_token');

// User storage
const getStoredUser = () => {
  const user = localStorage.getItem('heyday_user');
  return user ? JSON.parse(user) : null;
};
const setStoredUser = (user) => localStorage.setItem('heyday_user', JSON.stringify(user));
const clearStoredUser = () => { localStorage.removeItem('heyday_user'); localStorage.removeItem('heyday_companies'); try { sessionStorage.removeItem('sh_modules'); } catch (e) { /* ignore */ } };

// Companies this person can work in (one login, many companies)
export const getMyCompanies = () => {
  try { return JSON.parse(localStorage.getItem('heyday_companies') || '[]'); } catch (e) { return []; }
};
const setMyCompanies = (list) => { if (Array.isArray(list)) localStorage.setItem('heyday_companies', JSON.stringify(list)); };

// Move to another company: new token for that company, then a clean reload so every page shows its data
export const switchCompany = async (companyId) => {
  const data = await apiRequest('/api/auth/switch-company', { method: 'POST', body: JSON.stringify({ company_id: companyId }) });
  setToken(data.token);
  setStoredUser(data.user);
  setMyCompanies(data.companies);
  try { sessionStorage.clear(); } catch (e) { /* ignore */ }
  window.location.href = '/dashboard';
  return data;
};

// API helper with auth header
export const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const method = (options.method || 'GET').toUpperCase();
  const headers = {
    // Only set Content-Type for requests that have a body
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // An expired session goes back to sign-in. A wrong password on sign-in (or on accepting an invite)
  // is just an error message, never a page reload.
  const isAuthAttempt = endpoint.startsWith('/api/auth/login') || endpoint.includes('/invites/accept');
  if (response.status === 401 && token && !isAuthAttempt) {
    clearToken();
    clearStoredUser();
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    try {
      const error = await response.json();
      throw new Error(error.detail || 'Request failed');
    } catch (e) {
      if (e.message !== 'Request failed') throw e;
      throw new Error(`Request failed (${response.status})`);
    }
  }

  return response.json();
};

// Login
export const login = async ({ email, password }) => {
  const data = await apiRequest('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  setToken(data.token);
  setStoredUser(data.user);
  setMyCompanies(data.companies);
  // Notify ProjectContext that auth is ready so it reloads projects
  window.dispatchEvent(new CustomEvent('heyday:login'));
  return data;
};

// Logout
export const logout = () => {
  clearToken();
  clearStoredUser();
  window.location.href = '/login';
};

// Get current user
export const getCurrentUser = () => {
  return getStoredUser();
};

// Check if authenticated
export const isAuthenticated = () => {
  return !!getToken() && !!getStoredUser();
};

// Get auth token for API calls
export const getAccessToken = () => {
  return getToken() || null;
};

// Legacy dev mode check (always false now)
export const isDevMode = false;

// ── Invite helpers ────────────────────────────────────────────────────────────

export const getInviteInfo = async (token) => {
  return apiRequest(`/api/auth/invites/info/${token}`);
};

export const acceptInvite = async (token, { full_name, password }) => {
  const data = await apiRequest(`/api/auth/invites/accept/${token}`, {
    method: 'POST',
    body: JSON.stringify({ full_name, password }),
  });
  setToken(data.token);
  setStoredUser(data.user);
  setMyCompanies(data.companies);
  return data;
};

export const createInvite = async ({ email, role, project_ids = [], can_access_signal = true }) => {
  return apiRequest('/api/auth/invites', {
    method: 'POST',
    body: JSON.stringify({ email, role, project_ids, can_access_signal }),
  });
};

export const listInvites = async () => {
  return apiRequest('/api/auth/invites');
};

export const revokeInvite = async (inviteId) => {
  return apiRequest(`/api/auth/invites/${inviteId}`, { method: 'DELETE' });
};

export const listTeam = async () => {
  return apiRequest('/api/auth/team');
};

// Lightweight roster (name + email) any authenticated team member can call —
// use this for assignment dropdowns. listTeam() above is admin/owner-only.
export const listTeamRoster = async () => {
  return apiRequest('/api/auth/team/roster');
};

export const updateTeamMember = async (userId, data) => {
  return apiRequest(`/api/auth/team/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

// ── Company management (super admin) ─────────────────────────────────────────

export const listCompanies = async () => apiRequest('/api/companies');

export const createCompany = async (data) =>
  apiRequest('/api/companies', { method: 'POST', body: JSON.stringify(data) });

export const getCompany = async (id) => apiRequest(`/api/companies/${id}`);

export const updateCompany = async (id, data) =>
  apiRequest(`/api/companies/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

export const deleteCompany = async (id) =>
  apiRequest(`/api/companies/${id}`, { method: 'DELETE' });

export const inviteToCompany = async (companyId, data) =>
  apiRequest(`/api/companies/${companyId}/invites`, { method: 'POST', body: JSON.stringify(data) });

export const revokeCompanyInvite = async (companyId, inviteId) =>
  apiRequest(`/api/companies/${companyId}/invites/${inviteId}`, { method: 'DELETE' });

export const updateCompanyMember = async (companyId, userId, data) =>
  apiRequest(`/api/companies/${companyId}/members/${userId}`, { method: 'PATCH', body: JSON.stringify(data) });

export const isSuperAdmin = () => {
  const user = JSON.parse(localStorage.getItem('heyday_user') || '{}');
  return !!user?.is_super_admin;
};

// Returns true if user can see Campaigns + Social Calendar
export const canAccessSignal = () => {
  const user = JSON.parse(localStorage.getItem('heyday_user') || '{}');
  // owners and admins always have access; members respect the flag
  if (user?.role === 'owner' || user?.role === 'admin') return true;
  return user?.can_access_signal !== false;
};

// Returns the list of project IDs this user is scoped to (empty = all)
export const getUserProjectIds = () => {
  const user = JSON.parse(localStorage.getItem('heyday_user') || '{}');
  return user?.project_ids || [];
};

// Initialize auth (check if token is valid)
export const initializeAuth = async () => {
  const token = getToken();
  if (!token) return false;

  try {
    const data = await apiRequest('/api/auth/me');
    setStoredUser(data.user);
    setMyCompanies(data.companies);
    return true;
  } catch (error) {
    clearToken();
    clearStoredUser();
    return false;
  }
};

// ── Modules (super admin) ─────────────────────────────────────────────────────
export const getModuleRegistry = async () => apiRequest('/api/companies/modules/registry');

export const setCompanyModules = async (companyId, data) =>
  apiRequest(`/api/companies/${companyId}/modules`, { method: 'PUT', body: JSON.stringify(data) });
